import axios from 'axios'
import { useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Modal, type ButtonProps } from 'react-bootstrap'

type SignOutButtonProps = Omit<ButtonProps, 'onClick'> & {
  appName: string
  onConfirm: () => unknown | Promise<unknown>
  children?: ReactNode
}

// Keep this component identical across Accounts, CRM, Workflow and CMS.
export function SignOutButton({
  appName,
  onConfirm,
  children,
  disabled,
  ...props
}: SignOutButtonProps) {
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const cancelRef = useRef<HTMLButtonElement>(null)
  const pending = useRef(false)
  const close = () => {
    if (!pending.current) setShow(false)
  }
  const confirm = async () => {
    if (pending.current) return
    pending.current = true
    setBusy(true)
    setError('')
    try {
      await onConfirm()
      setShow(false)
    } catch (reason) {
      const detail = axios.isAxiosError(reason) ? reason.response?.data?.detail : undefined
      setError(
        typeof detail === 'string'
          ? detail
          : reason instanceof Error
            ? reason.message
            : 'Unable to sign out. Please try again.',
      )
    } finally {
      pending.current = false
      setBusy(false)
    }
  }
  return (
    <>
      <Button
        {...props}
        type="button"
        disabled={disabled || busy}
        onClick={() => {
          setError('')
          setShow(true)
        }}
      >
        {children || 'Sign out'}
      </Button>
      <Modal
        show={show}
        onHide={close}
        onEntered={() => cancelRef.current?.focus()}
        centered
        keyboard={!busy}
        aria-label={`Sign out of ${appName}?`}
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Sign out of {appName}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You’ll need to sign in again to continue using {appName}. Save any unsaved work before
            signing out.
          </p>
          {error && (
            <Alert variant="danger" role="alert">
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            ref={cancelRef}
            autoFocus
            variant="outline-secondary"
            disabled={busy}
            onClick={close}
          >
            Stay signed in
          </Button>
          <Button type="button" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Signing out…' : 'Sign out'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
